import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const PushDetail = ({ messageId, onBack, onRefresh }) => {
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
  });
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ type: '', text: '' });

  // 메시지 조회
  const fetchMessage = async () => {
    if (!messageId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('push_messages')
        .select('*')
        .eq('id', messageId)
        .single();

      if (error) throw error;
      if (data) {
        setMessage(data);
        setFormData({
          title: data.title || '',
          body: data.body || '',
        });
      }
    } catch (e) {
      console.error(e);
      setAlert({ type: 'danger', text: '메시지 조회 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessage();
  }, [messageId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // 저장
  const handleSave = async () => {
    if (!formData.title.trim()) {
      setAlert({ type: 'warning', text: '제목을 입력해주세요.' });
      return;
    }

    setSaving(true);
    setAlert({ type: '', text: '' });
    try {
      const { error } = await supabase
        .from('push_messages')
        .update({
          title: formData.title.trim(),
          body: formData.body.trim() || null,
        })
        .eq('id', messageId);

      if (error) throw error;
      setAlert({ type: 'success', text: '메시지가 저장되었습니다.' });
      setEditing(false);
      fetchMessage();
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error(e);
      setAlert({ type: 'danger', text: '저장 중 오류가 발생했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  // 삭제
  const handleDelete = async () => {
    if (!window.confirm('이 메시지를 삭제하시겠습니까?')) {
      return;
    }

    setSaving(true);
    setAlert({ type: '', text: '' });
    try {
      const { error } = await supabase
        .from('push_messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;
      setAlert({ type: 'success', text: '메시지가 삭제되었습니다.' });
      setTimeout(() => {
        onBack();
        if (onRefresh) onRefresh();
      }, 500);
    } catch (e) {
      console.error(e);
      setAlert({ type: 'danger', text: '삭제 중 오류가 발생했습니다.' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData({
      title: message?.title || '',
      body: message?.body || '',
    });
  };

  if (loading) {
    return (
      <div className="card shadow">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">로드 중...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="card shadow">
        <div className="card-body text-center py-5">
          <p className="text-muted">메시지를 찾을 수 없습니다.</p>
          <button className="btn btn-secondary btn-sm mt-3" onClick={onBack}>
            돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow">
      <div className="card-header py-3 bg-light d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">메시지 상세 정보</h6>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>
          <i className="fas fa-arrow-left"></i> 목록으로
        </button>
      </div>

      <div className="card-body">
        {alert.text && (
          <div className={`alert alert-${alert.type} alert-dismissible fade show`} role="alert">
            {alert.text}
            <button type="button" className="close" onClick={() => setAlert({ type: '', text: '' })}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        )}

        {/* 정보 영역 */}
        <div className="mb-4 pb-3 border-bottom">
          <h6 className="mb-3">기본 정보</h6>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-2">
                <label className="form-label small text-muted">상태</label>
                <p className="mb-0">
                  <span className="badge bg-secondary">
                    {message.status === 'pending' ? '대기' : message.status === 'sent' ? '발송완료' : '실패'}
                  </span>
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-2">
                <label className="form-label small text-muted">대상</label>
                <p className="mb-0">{message.target_type === 'all' ? '전체' : '특정 사용자'}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-0">
                <label className="form-label small text-muted">등록 시각</label>
                <p className="mb-0">
                  {message.created_at ? new Date(message.created_at).toLocaleString('ko-KR') : '-'}
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-0">
                <label className="form-label small text-muted">발송 시각</label>
                <p className="mb-0">
                  {message.sent_at ? new Date(message.sent_at).toLocaleString('ko-KR') : '-'}
                </p>
              </div>
            </div>
          </div>
          {message.success_count != null && (
            <div className="row mt-2">
              <div className="col-md-12">
                <label className="form-label small text-muted">발송 결과</label>
                <p className="mb-0">성공: {message.success_count}건 / 실패: {message.fail_count}건</p>
              </div>
            </div>
          )}
        </div>

        {/* 편집 영역 */}
        {editing ? (
          <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div className="mb-3">
              <label htmlFor="msg-title" className="form-label">제목 *</label>
              <input
                id="msg-title"
                type="text"
                className="form-control"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="메시지 제목"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="msg-body" className="form-label">내용</label>
              <textarea
                id="msg-body"
                className="form-control"
                rows={5}
                name="body"
                value={formData.body}
                onChange={handleInputChange}
                placeholder="메시지 내용"
              />
            </div>

            <div className="d-flex" style={{ gap: '0.5rem' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                취소
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="mb-3">
              <label className="form-label">제목</label>
              <p className="form-control-plaintext">{formData.title}</p>
            </div>

            <div className="mb-3">
              <label className="form-label">내용</label>
              <p className="form-control-plaintext" style={{ whiteSpace: 'pre-wrap' }}>
                {formData.body || '(내용 없음)'}
              </p>
            </div>

            <div className="mb-3">
              <label className="form-label">발송 시간대</label>
              <p className="form-control-plaintext">
                {message.target_cd === null || message.target_cd === ''
                  ? '잠금'
                  : message.target_cd === 'm'
                  ? '아침'
                  : message.target_cd === 'a'
                  ? '점심'
                  : message.target_cd === 'e'
                  ? '저녁'
                  : message.target_cd}
              </p>
            </div>

            <div className="d-flex" style={{ gap: '0.5rem' }}>
              <button
                className="btn btn-warning btn-sm"
                onClick={() => setEditing(true)}
                disabled={saving}
              >
                <i className="fas fa-edit"></i> 수정
              </button>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleDelete}
                disabled={saving}
              >
                <i className="fas fa-trash"></i> 삭제
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PushDetail;
